import { Trash, Users } from "lucide-react"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { EducatorsSearchProvider } from "@/components/search/educators/educator-search-provider"
import {
  EducatorResultContent,
  EducatorResultFooter,
  EducatorResultHeader,
  EducatorResultSpecialities,
  EducatorResultTags,
  type EducatorSearchHit,
  EducatorSearchResults,
  EducatorShortlistResultCard,
} from "@/components/search/educators/educator-search-result"
import { EducatorSearchFacets } from "@/components/search/educators/educator-search-sidebar"
import { ShortlistedSearchWorkspace } from "@/components/search/shortlisted-search-workspace"
import { SearchDetailLink } from "@/components/search/search-return-navigation"
import { Sidebar } from "@/components/search/search-layout"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { Separator } from "@/components/ui/separator"
import { SearchUrlState } from "@/lib/redux-search/search-url-state"

import { type IndexProps } from "./page-props"
import { Link, useProps } from "@/lib/page-rendering"

// The shortlist is read from this component's own props and threaded down
// rather than pulled from `usePage`, so the page behaves the same when it is
// rendered as an embedded page inside a sheet.
export default function Page() {
  const { shortlist } = useProps<IndexProps>()
  const ResultComponent = useCallback(
    ({ result }: { result: EducatorSearchHit }) => (
      <Result result={result} shortlistId={shortlist.id} />
    ),
    [shortlist.id],
  )

  return (
    <EducatorsSearchProvider
      url={`/search/shortlists/${shortlist.id}/educators/query`}
    >
      <SearchUrlState />
      <ShortlistedSearchWorkspace
        activeTab="educators"
        results={<EducatorSearchResults ResultComponent={ResultComponent} />}
        shortlist={shortlist}
        sidebar={
          <Sidebar>
            <EducatorSearchFacets />
          </Sidebar>
        }
      />
    </EducatorsSearchProvider>
  )
}

function Result({
  result,
  shortlistId,
}: {
  result: EducatorSearchHit
  shortlistId: string
}) {
  const { t } = useTranslation(["shortlists", "common"])

  return (
    <EducatorShortlistResultCard result={result}>
      <EducatorResultHeader result={result} />
      <EducatorResultContent>
        <EducatorResultSpecialities result={result} />
        <Separator />
        <EducatorResultTags result={result} />
      </EducatorResultContent>
      <EducatorResultFooter>
        <Button asChild>
          <SearchDetailLink href={`/search/educators/${result.id}`}>
            <Users />
            {t("shortlists:workspace.educators.view_educator")}
          </SearchDetailLink>
        </Button>
        <ConfirmationDialog
          action={
            <Link
              as={Button}
              className={buttonVariants({ variant: "destructive" })}
              href={`/search/shortlists/${shortlistId}/educators/${result.id}`}
              method="delete"
            >
              {t("common:actions.confirm")}
            </Link>
          }
        >
          <ConfirmationDialogTrigger asChild>
            <Button variant="destructive-outline">
              <Trash />
              {t("common:actions.remove")}
            </Button>
          </ConfirmationDialogTrigger>
        </ConfirmationDialog>
      </EducatorResultFooter>
    </EducatorShortlistResultCard>
  )
}
